import React from "react";
import { useState } from "react";
import userJSON from "../../../utils/userJSON";
import sendServerLogging from "../../../utils/sendServerLogging";

export default function UploadMacrosPanel({ userId }) {
  const [file, setFile] = useState(null);
  const [macroEntries, setMacroEntries] = useState([
    {
      id: Date.now(),
      dotPhrase: "",
      keywords: "",
      value: "",
    },
  ]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });

  const handleInputChange = (id, field, value) => {
    setMacroEntries((prev) =>
      prev.map((entry) => {
        if (entry.id !== id) return entry;

        // If it's the dotPhrase field and doesn't start with ".", add it
        if (field === "dotPhrase" && value && !value.startsWith(".")) {
          value = "." + value;
        }

        return { ...entry, [field]: value };
      })
    );
  };

  const addAnotherMacro = () => {
    const newId = Date.now();
    setMacroEntries((prev) => [
      ...prev,
      {
        id: newId,
        dotPhrase: "",
        keywords: "",
        content: "",
      },
    ]);

    setTimeout(() => {
      const container = document.getElementById("macro-entries-container");
      const newElement = document.getElementById(`macro-entry-${newId}`);
      if (container && newElement) {
        container.scrollTop = newElement.offsetTop - container.offsetTop;
      }
    }, 100);
  };

  const removeMacro = (id) => {
    if (macroEntries.length > 1) {
      setMacroEntries((prev) => prev.filter((entry) => entry.id !== id));
    }
  };

  const isFormValid = () => {
    const hasCompleteFormData = macroEntries.some(
      (entry) => entry.dotPhrase && entry.keywords && entry.content
    );
    return hasCompleteFormData;
  };

  const handleSubmit = async () => {
    if (!isFormValid()) return;

    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    const completeMacros = macroEntries.filter(
      (entry) => entry.dotPhrase && entry.keywords && entry.content
    );

    try {
      // Create an array of promises for all macro submissions
      const submissions = await Promise.all(
        completeMacros.map(async (macro) => {
          // Convert keywords string to array (split by comma and trim whitespace)
          const keywordsArray = macro.keywords
            .split(",")
            .map((keyword) => keyword.trim())
            .filter((keyword) => keyword.length > 0);

          const payload = {
            doctor_id: userId,
            dot_phrase: macro.dotPhrase,
            keywords: keywordsArray,
            content: macro.content,
          };

          const response = await fetch(
            `${process.env.REACT_APP_API_URL}/macros/new-macro`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                token: localStorage.token,
              },
              body: JSON.stringify(payload),
            }
          );

          //server logging
          sendServerLogging({
            body: {
              ...userJSON(localStorage.getItem("userName")),
              event: "MACRO_CREATED",
              eventData: {
                dot_phrase: macro.dotPhrase,
                keywords: keywordsArray,
                content: macro.content,
              },
            },
          });

          return response;
        })
      );
      // Wait for all submissions to complete
      const responses = await Promise.all(submissions);

      // Check if all requests were successful
      const allSuccessful = responses.every((response) => response.ok);

      if (allSuccessful) {
        setSubmitStatus({
          type: "success",
          message: `Successfully uploaded ${completeMacros.length} macro${
            completeMacros.length > 1 ? "s" : ""
          }!`,
        });

        // Reset form after successful submission
        setFile(null);
        setMacroEntries([
          { id: Date.now(), dotPhrase: "", keywords: "", content: "" },
        ]);
      } else {
        // Handle partial failures
        const failedCount = responses.filter((r) => !r.ok).length;
        setSubmitStatus({
          type: "error",
          message: `Failed to upload ${failedCount} macro${
            failedCount > 1 ? "s" : ""
          }. Please try again.`,
        });
      }
    } catch (error) {
      console.error("Error submitting macros:", error);
      setSubmitStatus({
        type: "error",
        message: "An error occurred while uploading macros. Please try again.",
      });
      //server logging
      sendServerLogging({
        body: {
          ...userJSON(localStorage.getItem("userName")),
          event: "ERROR_CREATING_MACRO",
          eventData: { error_message: error.message.toString() },
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <h2 className="text-xl text-purple-700 font-bold mb-2">
        Add Custom Macros
      </h2>
      <p className="text-gray-600 text-sm italic mb-6">
        Enter the details for the custom macro you would like to add.
      </p>

      {/* Status Message */}
      {submitStatus.message && (
        <div
          className={`mb-4 p-3 rounded-md ${
            submitStatus.type === "success"
              ? "bg-green-100 text-green-800 border border-green-300"
              : "bg-red-100 text-red-800 border border-red-300"
          }`}
        >
          {submitStatus.message}
        </div>
      )}

      <div className="w-full mb-6">
        <h3 className="text-lg font-semibold mb-3">Enter Macro Details</h3>
        <div
          className="space-y-6 max-h-96 overflow-y-auto pr-2"
          id="macro-entries-container"
        >
          {macroEntries.map((entry, index) => (
            <div
              key={entry.id}
              id={`macro-entry-${entry.id}`}
              className="relative p-4 border border-gray-200 rounded-lg space-y-4"
            >
              {macroEntries.length > 1 && (
                <button
                  onClick={() => removeMacro(entry.id)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-red-700"
                  title="Remove this macro"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              )}

              {macroEntries.length > 1 && (
                <h3 className="font-bold text-purple-700">Macro {index + 1}</h3>
              )}

              <div>
                <label
                  htmlFor={`dotPhrase-${entry.id}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Dot Phrase <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id={`dotPhrase-${entry.id}`}
                  value={entry.dotPhrase}
                  onChange={(e) =>
                    handleInputChange(entry.id, "dotPhrase", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter dot phrase"
                />
              </div>

              <div>
                <label
                  htmlFor={`keywords-${entry.id}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Keywords <span className="text-red-500">*</span>
                </label>
                <p className="text-xs text-gray-500 mb-2 italic">
                  Tip: Keywords should capture the macro's purpose and use cases
                </p>
                <input
                  type="text"
                  id={`keywords-${entry.id}`}
                  value={entry.keywords}
                  onChange={(e) =>
                    handleInputChange(entry.id, "keywords", e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Enter keywords (comma separated)"
                />
              </div>

              <div>
                <label
                  htmlFor={`value-${entry.id}`}
                  className="block text-sm font-medium text-gray-700 mb-1"
                >
                  Content <span className="text-red-500">*</span>
                </label>
                <textarea
                  id={`content-${entry.id}`}
                  value={entry.content}
                  onChange={(e) =>
                    handleInputChange(entry.id, "content", e.target.value)
                  }
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                  placeholder="Enter macro content"
                />
              </div>
            </div>
          ))}
        </div>

        <button
          onClick={addAnotherMacro}
          className="mt-4 flex items-center gap-2 text-purple-600 hover:text-purple-800 font-medium"
        >
          <svg
            className="w-5 h-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          Add Another Macro
        </button>
      </div>

      <div className="flex gap-3 justify-end">
        <button
          onClick={handleSubmit}
          disabled={!isFormValid() || isSubmitting}
          className={`px-6 py-2 rounded-md transition-colors ${
            isFormValid() && !isSubmitting
              ? "bg-purple-600 text-white hover:bg-purple-700"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
          }`}
        >
          {isSubmitting ? "Submitting..." : "Submit"}
        </button>
      </div>
    </div>
  );
}
